/**
 * Copy for the bulk-action confirmations in `BulkActionsBar`.
 *
 * Every bulk mutation quotes a count back at the user, so the pluralisation ran
 * inline in eight places and had already drifted once ("Updated status for 1
 * contacts"). Building the strings here keeps the count agreement in one tested
 * place, and keeps the descriptions honest about what survives each action —
 * which is the whole point of the confirmation.
 */

/** `1 contact` / `3 contacts` — the phrase every description and toast needs. */
export function contactCount(count: number): string {
  return `${count} contact${count === 1 ? '' : 's'}`;
}

/** Subject pronoun for the same count, so descriptions can read naturally. */
export function contactPronoun(count: number): 'it' | 'them' {
  return count === 1 ? 'it' : 'them';
}

export interface BulkConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
}

export function deleteConfirmCopy(count: number): BulkConfirmCopy {
  return {
    title: 'Delete contacts',
    description:
      `The selected ${contactCount(count)} will be permanently removed from your ` +
      'Contacts section. This action cannot be undone. To only take ' +
      `${contactPronoun(count)} out of a list or tag instead, use ` +
      '"Remove from List" or "Remove from Tag".',
    confirmLabel: 'Delete',
  };
}

export function untagConfirmCopy(tag: string, count: number): BulkConfirmCopy {
  return {
    title: 'Remove tag',
    description:
      `Remove “${tag}” from ${contactCount(count)}? They stay in your contacts ` +
      'and keep their other tags — only this tag comes off.',
    confirmLabel: 'Remove tag',
  };
}

export function removeFromListConfirmCopy(listName: string, count: number): BulkConfirmCopy {
  return {
    title: 'Remove from list',
    description:
      `Remove ${contactCount(count)} from “${listName}”? They stay in your contacts ` +
      "and in any other list — only this list's membership changes.",
    confirmLabel: 'Remove',
  };
}

export function statusChangeConfirmCopy(status: string, count: number): BulkConfirmCopy {
  return {
    title: 'Change lead status',
    description:
      `Set the lead status of ${contactCount(count)} to “${status}”? This overwrites ` +
      `whatever status ${count === 1 ? 'it has' : 'they have'} now; nothing else ` +
      'about the contacts changes.',
    confirmLabel: 'Change status',
  };
}
